import * as React from "react"
import { Minus, Plus, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { UnitPicker } from "./unit-picker"

const HINT_STORAGE_KEY = 'kwaime-unit-switcher-hint-seen'

export interface TuneChipProps {
  value: number | null
  unit: string
  units?: string[]
  onUnitChange?: (unit: string) => void
  onChange: (value: number | null) => void
  step?: number
  min?: number
  max?: number
  allowNull?: boolean
  variant?: 'default' | 'secondary'
  className?: string
}

export const TuneChip = React.forwardRef<HTMLDivElement, TuneChipProps>(
  ({ 
    value, 
    unit, 
    units, 
    onUnitChange, 
    onChange, 
    step = 1, 
    min, 
    max, 
    allowNull = false,
    variant = 'default',
    className,
    ...props 
  }, ref) => {
    const [showUnitPicker, setShowUnitPicker] = React.useState(false)
    const [showHint, setShowHint] = React.useState(false)

    // Check if hint should be shown (one-time only)
    React.useEffect(() => {
      const hasUnitSwitcher = units && units.length > 1 && onUnitChange
      if (hasUnitSwitcher) {
        const hintSeen = localStorage.getItem(HINT_STORAGE_KEY)
        if (!hintSeen) {
          // Show hint after a short delay
          const timer = setTimeout(() => setShowHint(true), 1000)
          return () => clearTimeout(timer)
        }
      }
    }, [units, onUnitChange])

    const dismissHint = () => {
      setShowHint(false)
      localStorage.setItem(HINT_STORAGE_KEY, 'true')
    }

    const handleDecrement = () => {
      if (value === null) {
        onChange(min ?? 0)
        return
      }
      
      const newValue = value - step
      
      if (allowNull && min !== undefined && newValue < min) {
        onChange(null)
        return
      }
      
      if (min !== undefined && newValue < min) {
        onChange(min)
        return
      }
      
      onChange(newValue)
    }

    const handleIncrement = () => {
      if (value === null) {
        onChange(min ?? step)
        return
      }
      
      const newValue = value + step
      
      if (max !== undefined && newValue > max) {
        onChange(max)
        return
      }
      
      onChange(newValue)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      
      if (inputValue === '' && allowNull) {
        onChange(null)
        return
      }
      
      const parsed = parseFloat(inputValue)
      if (isNaN(parsed)) return
      
      if (min !== undefined && parsed < min) {
        onChange(min)
        return
      }
      
      if (max !== undefined && parsed > max) {
        onChange(max)
        return
      }
      
      onChange(parsed)
    }

    const handleBlur = () => {
      if (value === null && !allowNull) {
        onChange(min ?? 0)
      }
    }

    const handleUnitClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      dismissHint()
      setShowUnitPicker(!showUnitPicker)
    }

    const isSecondary = variant === 'secondary'
    const hasUnitSwitcher = units && units.length > 1 && onUnitChange
    const canDecrement = allowNull || value === null || (min === undefined) || (value > min)
    const canIncrement = value === null || (max === undefined) || (value < max)

    return (
      <div 
        ref={ref} 
        className={cn("relative w-full", className)}
        {...props}
      >
        {/* 3-column grid: [-] [value + unit] [+] */}
        <div className={cn(
          "grid grid-cols-[40px_1fr_40px] items-center h-10 rounded-xl transition-colors",
          "w-full",
          isSecondary 
            ? "bg-[var(--surface)]" 
            : "bg-neutral-100/60"
        )}>
          {/* Minus button */}
          <button
            type="button"
            onClick={handleDecrement}
            disabled={!canDecrement}
            className={cn(
              "h-full flex items-center justify-center rounded-l-xl transition-all cursor-pointer",
              "text-neutral-400 hover:text-neutral-600 hover:bg-[var(--surface-strong)] active:bg-neutral-200/80 active:scale-95",
              "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:scale-100",
              "focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
            )}
          >
            <Minus className="w-4 h-4" strokeWidth={1.5} />
          </button>
          
          {/* Value + Unit block - fixed layout for alignment */}
          <div className="flex items-center justify-center h-full relative">
            {/* Fixed-width container for value + unit */}
            <div className="flex items-center">
              {/* Value - fixed width for vertical alignment */}
              <div className="w-14 flex justify-end">
                <input
                  type="number"
                  value={value ?? ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={allowNull ? "—" : undefined}
                  className={cn(
                    "w-full text-right bg-transparent border-0 pr-1",
                    "text-base font-semibold text-[var(--text-strong)]",
                    "focus:outline-none focus:ring-0",
                    "placeholder:text-neutral-300 placeholder:font-normal",
                    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  )}
                />
              </div>
              
              {/* Unit - fixed width for vertical alignment */}
              <div className="w-14 flex justify-start">
                {unit && (
                  hasUnitSwitcher ? (
                    <button
                      type="button"
                      onClick={handleUnitClick}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 rounded-full",
                        "bg-neutral-200/70 hover:bg-neutral-300/70 active:bg-neutral-300 active:scale-95",
                        "transition-all cursor-pointer",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/50"
                      )}
                    >
                      <span className="text-sm font-semibold text-neutral-700">
                        {unit}
                      </span>
                      <ChevronDown 
                        className={cn(
                          "w-3 h-3 text-neutral-500 transition-transform duration-200 flex-shrink-0",
                          showUnitPicker && "rotate-180"
                        )} 
                      />
                    </button>
                  ) : (
                    <span className="text-sm font-semibold text-neutral-500 pl-1">
                      {unit}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Unit Picker Dropdown */}
            {hasUnitSwitcher && units && (
              <UnitPicker
                units={units}
                selectedUnit={unit}
                onSelect={(newUnit) => onUnitChange?.(newUnit)}
                isOpen={showUnitPicker}
                onOpenChange={setShowUnitPicker}
              />
            )}

            {/* One-time hint tooltip */}
            {showHint && hasUnitSwitcher && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 animate-in fade-in-0 slide-in-from-top-1 duration-200"
                onClick={dismissHint}
              >
                <div className="bg-neutral-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-[200px] text-center">
                  <p>Tap <span className="font-semibold">{unit}</span> to switch to {unit === 'reps' ? 'time-based' : 'rep-based'} work</p>
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-neutral-800 rotate-45" />
                </div>
              </div>
            )}
          </div>
          
          {/* Plus button */}
          <button
            type="button"
            onClick={handleIncrement}
            disabled={!canIncrement}
            className={cn(
              "h-full flex items-center justify-center rounded-r-xl transition-all cursor-pointer",
              "text-neutral-400 hover:text-neutral-600 hover:bg-[var(--surface-strong)] active:bg-neutral-200/80 active:scale-95",
              "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:scale-100",
              "focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
            )}
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    )
  }
)

TuneChip.displayName = "TuneChip"
